import { useState } from 'react';
import { AIRTIME_MAX, AIRTIME_MIN, AUTOTOPUP_MAX_PER_DAY, consentText } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import {
  useAutoTopup, usePauseAutoTopup, useRemoveCard, useSaveCard,
} from '../../lib/api/hooks-autotopup.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { Badge, ErrorBanner } from '../../components/ui/Primitives.js';
import { formatDateTime, formatMoney } from '../../lib/format.js';
import { toPayfast } from './Payments.js';

/**
 * Auto top-up (docs/28 B3): a prepaid client's card, charged a fixed amount when the
 * balance falls below its level. The client saves the card while buying airtime, in
 * words it ticks and which are kept. Staff see it and can turn it off.
 */

const TZ = 'Africa/Johannesburg';

export function AutoTopupCard({ tenantId, staff }: { tenantId: string; staff: boolean }) {
  const can = useSession((s) => s.can);
  const status = useAutoTopup(tenantId);
  const save = useSaveCard(tenantId);
  const pause = usePauseAutoTopup(tenantId);
  const remove = useRemoveCard(tenantId);
  const step = useStepUp();
  const [open, setOpen] = useState(false);
  const [threshold, setThreshold] = useState('100');
  const [amount, setAmount] = useState('500');
  const [perDay, setPerDay] = useState(2);
  const [agreed, setAgreed] = useState(false);

  const a = status.data;
  const ownsIt = !staff && can('payment:make');
  const mayRemove = ownsIt || (staff && can('billing:write'));
  const failed = [save.error, pause.error, remove.error]
    .find((e): e is ApiClientError => e instanceof ApiClientError && e.code !== 'auth.step_up_required') ?? null;

  if (status.error) return <div style={{ marginTop: 16 }}><ErrorBanner error={status.error} /></div>;
  if (!a) return null;
  // Nothing to offer a client and nothing saved: no card at all. Staff are told why.
  if (!a.available && !a.card && !staff) return null;

  const leaving = save.isPending || save.isSuccess;
  const words = consentText(a.issuer_name ?? 'the provider', amount.trim() || '0', threshold.trim() || '0', perDay);

  const submit = () => save.mutate(
    { threshold: threshold.trim(), amount: amount.trim(), max_per_day: perDay, consent: true },
    { onSuccess: toPayfast },
  );
  const turnOff = () => void step.run(() => remove.mutateAsync()).catch(() => undefined);

  return (
    <div className="arc-card" style={{ marginTop: 16 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">Auto top-up</h2>
        <div className="arc-row" style={{ gap: 6 }}>
          {a.card?.mode === 'sandbox' && <Badge tone="info">test card</Badge>}
          {a.card?.status === 'active' && (a.enabled ? <Badge tone="ok">on</Badge> : <Badge tone="muted">paused</Badge>)}
        </div>
      </div>
      <div className="arc-card-body">
        {failed && <ErrorBanner error={failed} />}
        <StepUpPrompt step={step} what="Turning auto top-up off" />

        {a.card?.status === 'pending' ? (
          <p className="arc-muted" style={{ margin: 0 }}>Waiting for PayFast to confirm the card…</p>
        ) : a.card?.status === 'active' ? (
          <>
            <p style={{ marginTop: 0 }}>
              When the balance falls below {formatMoney(a.threshold, 'ZAR')}, {formatMoney(a.amount, 'ZAR')} is
              added by card, at most {a.max_per_day} {a.max_per_day === 1 ? 'time' : 'times'} a day.
              {a.today > 0 && ` ${a.today} today so far.`}
            </p>
            <p className="arc-hint">
              {a.card.consent_text}
              {a.card.saved_at && ` Agreed ${formatDateTime(a.card.saved_at, TZ)}.`}
            </p>
            {a.last_failure && (
              <p className="arc-muted">
                Last refused {formatDateTime(a.last_failure.at, TZ)}: {a.last_failure.error}
              </p>
            )}
            <div className="arc-row" style={{ gap: 8 }}>
              {ownsIt && (
                <button type="button" className="arc-btn arc-btn-sm" disabled={pause.isPending}
                  onClick={() => pause.mutate(!a.enabled)}>
                  {a.enabled ? 'Pause' : 'Resume'}
                </button>
              )}
              {mayRemove && (
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={remove.isPending}
                  onClick={turnOff}>
                  Turn off and forget the card
                </button>
              )}
            </div>
          </>
        ) : !a.available ? (
          <p className="arc-muted" style={{ margin: 0 }}>{a.unavailable_reason}</p>
        ) : !ownsIt ? (
          <p className="arc-muted" style={{ margin: 0 }}>Off. The client sets it up from its own portal.</p>
        ) : !open ? (
          <div className="arc-row" style={{ gap: 12 }}>
            <span className="arc-muted">Top up by card automatically when the balance runs low.</span>
            <div className="arc-spacer" />
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => setOpen(true)}>Set up</button>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <div className="arc-grid arc-grid-2">
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="autotopup-threshold">When the balance falls below</label>
                <input id="autotopup-threshold" className="arc-input arc-num" inputMode="decimal" required
                  value={threshold} onChange={(e) => setThreshold(e.target.value)} />
              </div>
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="autotopup-amount">Add</label>
                <input id="autotopup-amount" className="arc-input arc-num" inputMode="decimal" required
                  value={amount} onChange={(e) => setAmount(e.target.value)} />
                <div className="arc-hint">R{AIRTIME_MIN} to R{AIRTIME_MAX}.</div>
              </div>
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="autotopup-per-day">At most, each day</label>
                <select id="autotopup-per-day" className="arc-select" value={perDay}
                  onChange={(e) => setPerDay(Number(e.target.value))}>
                  {Array.from({ length: AUTOTOPUP_MAX_PER_DAY }, (_, n) => n + 1).map((n) => (
                    <option key={n} value={n}>{n} {n === 1 ? 'time' : 'times'}</option>
                  ))}
                </select>
              </div>
            </div>

            <label className="arc-row" style={{ gap: 7, margin: '14px 0', alignItems: 'flex-start' }}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required />
              <span>{words}</span>
            </label>

            <div className="arc-row" style={{ gap: 8 }}>
              <button type="submit" className="arc-btn arc-btn-primary" disabled={!agreed || leaving}>
                {leaving ? 'Opening PayFast…' : `Save a card and buy ${formatMoney(amount.trim() || '0', 'ZAR')}`}
              </button>
              <button type="button" className="arc-btn arc-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            </div>
            <p className="arc-hint" style={{ marginTop: 10 }}>
              PayFast keeps the card. The first top-up is bought now, and the card is saved with it.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
